/**
 * Extract user-friendly error message from API error response
 * 
 * NOTE: Our API interceptor (api.js) already extracts error.response.data
 * So the error object we receive is already the data from the response
 * 
 * @param {Error} error - The error object from API call
 * @param {string} defaultMessage - Default message if no specific error found
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error, defaultMessage = 'An error occurred') => {
  // API interceptor extracts error.response.data, so check error.error first
  if (error.error?.message) {
    return error.error.message;
  }
  
  // Direct message property
  if (error.message) {
    return error.message;
  }
  
  // Fallback for non-intercepted errors
  if (error.response?.data?.error?.message) {
    return error.response.data.error.message;
  }
  
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  return defaultMessage;
};

/**
 * Get user-friendly error message for duplicate entry errors
 * @param {Error} error - The error object
 * @returns {string} Formatted error message
 */
export const getDuplicateErrorMessage = (error) => {
  const message = getErrorMessage(error);
  
  // If it's already a detailed message, return it
  if (message.includes('already registered') || message.includes('already exists')) {
    return message;
  }
  
  return message;
};

/**
 * Handle API errors with toast notifications
 * @param {Error} error - The error object
 * @param {Function} toast - Toast notification function
 * @param {string} defaultMessage - Default error message
 */
export const handleApiError = (error, toast, defaultMessage) => {
  const errorMessage = getErrorMessage(error, defaultMessage);
  console.error('API Error:', error);
  toast.error(errorMessage);
};

/**
 * Get user-friendly error message for company verification errors
 * @param {Error} error - The error object
 * @returns {string} Formatted error message
 */
export const getCompanyVerificationError = (error) => {
  const errorCode = error.error?.code || error.code;
  const errorMessage = error.error?.message || error.message || '';

  // Company verification specific errors
  if (errorCode === 'AUTHORIZATION_ERROR') {
    if (errorMessage.includes('under verification')) {
      return '⏳ Your company is under verification. You can post jobs once it is approved by admin.';
    }
    if (errorMessage.includes('create your company profile')) {
      return '🏢 Please create your company profile first before posting jobs.';
    }
    if (errorMessage.includes('rejected')) {
      return `❌ ${errorMessage}`;
    }
    if (errorMessage.includes('documents')) {
      return '📄 Additional documents are required for company verification. Please check your email or contact admin.';
    }
    if (errorMessage.includes('company')) {
      return errorMessage;
    }
    return 'You are not authorized to perform this action. Please verify your company profile.';
  }

  // Return original message for other errors
  return getErrorMessage(error, 'An error occurred');
};
